import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { faPalette } from "@fortawesome/free-solid-svg-icons";
import {
  IMenuItem,
  PluginInjectorApplicationPlugin,
  IDropDownMenuItem,
  IMenuItemPermissions,
} from "core-client/api";
import { BehaviorSubject } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class ThemeSwitcherService {
  private navbarMenuItem!: IMenuItem;
  private readonly navbarMenuTitle: string = "THEME_SWITCHER";
  private readonly pluginModule: string = "THEME_SWITCHER";

  private menuItemPermissions: IMenuItemPermissions;

  private themes: {
    [key: string]: { class: string | null; select: (() => void) | null };
  };
  private currentTheme: string | null;
  private html: HTMLHtmlElement | null = document.querySelector("html");

  constructor() {
    this.menuItemPermissions = {};
    this.themes = {
      light: { class: null, select: null },
      dark: { class: "dark", select: null },
      "red-green-color-blindness": {
        class: "red-green-color-blindness",
        select: null,
      },
    };
    this.currentTheme = null;

    const currentUiTheme =
      localStorage.getItem("currentUiTheme") ?? 'light';
    const theme = this.themes[currentUiTheme];
    if (this.html && theme?.class) {
      this.html.classList.add(theme.class);
      this.currentTheme = theme.class;
    }
  }

  getNavbarMenuItem(
    pluginConfig: PluginInjectorApplicationPlugin.IApplicationPlugin,
    themeSwitcherMenu: IMenuItem[],
    primaryEnabled: boolean,
    secondaryEnabled: boolean,
  ): IMenuItem[] {
    const menuItemsPermissionsRefs = this.getMenuItemPermissionRefs();

    const menuExist = themeSwitcherMenu.some(
      (menuItem: IMenuItem) =>
        menuItem.nameIdentifier.toLowerCase() ===
        this.navbarMenuTitle.toLowerCase(),
    );

    this.navbarMenuItem = {
      pluginId: pluginConfig._id,
      pluginName: pluginConfig.name,
      pluginModule: this.pluginModule,
      nameIdentifier: this.navbarMenuTitle,
      accessRights: menuItemsPermissionsRefs,
      primary: {
        enabled: primaryEnabled,
        position: themeSwitcherMenu.length + 1,
      },
      secondary: {
        enabled: secondaryEnabled,
        position: themeSwitcherMenu.length + 1,
      },
      strictMode: true,
      headerSortOrder: 60,
      icon: faPalette,
      dropdown: true,
      dropdownItems: [
        this.createMenuItem(
          "LIGHT_THEME",
          pluginConfig.name,
          "",
          "adx-example-theme",
          "light",
        ),
        this.createMenuItem(
          "DARK_THEME",
          pluginConfig.name,
          "",
          "adx-example-theme dark",
          "dark",
        ),
        this.createMenuItem(
          "RED_GREEN_COLOR_BLINDNESS",
          pluginConfig.name,
          "",
          "adx-example-theme red-green-color-blindness",
          "red-green-color-blindness",
        ),
      ],
      disabled: new BehaviorSubject(false),
    };

    if (!menuExist) {
      themeSwitcherMenu.push(this.navbarMenuItem);
    }

    return themeSwitcherMenu;
  }

  private getMenuItemPermissionRefs(): string[] {
    const menuItemsPermissionsRefs: string[] = [];

    for (const menuItemFlag in this.menuItemPermissions) {
      menuItemsPermissionsRefs.push(
        ...Object.keys(this.menuItemPermissions[menuItemFlag]),
      );
    }

    return menuItemsPermissionsRefs;
  }

  getMenuItemPermissions(): IMenuItemPermissions {
    return this.menuItemPermissions;
  }

  private setTheme(name: string) {
    if (this.html && !(this.themes[name] === undefined)) {
      if (this.currentTheme != null) {
        this.html.classList.remove(this.currentTheme);
      }
      let theme = this.themes[name].class;
      if (theme != null) {
        this.html.classList.add(theme);
      }
      this.currentTheme = theme;
      localStorage.setItem("currentUiTheme", name);
    }
  }

  private selectedTotal: { value: boolean }[] = [];

  private createMenuItem(
    nameIdentifier: string,
    pluginName: string,
    accessReference: string | string[],
    className: string,
    themeName: string,
  ): IDropDownMenuItem {
    const isCurrentTheme = this.currentTheme === this.themes[themeName]?.class;
    const selectedObject: { value: boolean } = { value: isCurrentTheme };
    const select = () => {
      this.selectedTotal.forEach((value: { value: boolean }) => {
        value.value = false;
      });
      selectedObject.value = true;
      this.setTheme(themeName);
    };
    const callback = (dialog: MatDialog, id?: string) => {
      select();
    };
    const theme = this.themes[themeName];
    if (theme) {
      theme.select = select;
    }
    this.selectedTotal.push(selectedObject);
    return {
      pluginName: pluginName,
      pluginModule: this.pluginModule,
      nameIdentifier,
      accessReference,
      strictMode: true,
      dropdown: false,
      dropdownItems: [],
      disabled: new BehaviorSubject(false),
      selected: () => selectedObject.value,
      class: className,
      callback: callback,
    };
  }
}
